// 투입중량 계산 팝업 — 원자재 1본(전장)의 중량을 1본 생산량으로 나눠 잔봉까지 반영한
// 투입중량(g)을 산출한다(내용추가18). 계산은 @/lib/cost/weightCalc 의 순수 함수 재사용.
// 팝업은 정밀값(9.6909)을 보여주고, 적용 시 메인 화면에는 소수 둘째 자리 버림값(9.69)을 저장한다.
import { Field, Icon, Select, TextInput } from "@/components/ui";
import type { CncMasterData, InputWeightApply } from "@/lib/cost/cncForm";
import { CUT_WIDTH_STEPS } from "@/lib/cost/material";
import {
  DEFAULT_REMNANT_LENGTH,
  DEFAULT_STOCK_LENGTH,
  INPUT_PI,
  cutLossForOuter,
  inputWeight,
} from "@/lib/cost/weightCalc";
import { cols, useIsMobile } from "@/lib/useViewport";
import { useState } from "react";

const fmtDec = (n: number, d = 4): string =>
  Number.isFinite(n) ? n.toLocaleString("ko-KR", { maximumFractionDigits: d }) : "—";

const num = (s: string): number => Number.parseFloat(s) || 0;

// 정수 표시(소수 버림) — 1본 중량·1본 자재원가 표기용. 내부 계산은 정밀값 유지.
const fmtInt = (n: number): string =>
  Number.isFinite(n) ? Math.floor(n).toLocaleString("ko-KR") : "—";

// 단가 단위 변환(₩/g ↔ 원/kg) — 1e-9 로 반올림해 0.29×1000=290.00000000000006 같은 잡음 제거.
const clean = (n: number): string => String(Math.round(n * 1e9) / 1e9);
const perKgFromPerG = (perG: string): string => (perG.trim() === "" ? "" : clean(num(perG) * 1000));

const cellR = { padding: "8px 12px", textAlign: "right" as const, fontFamily: "monospace" };
const cellL = { padding: "8px 12px", textAlign: "left" as const };

export const InputWeightCalcModal = ({
  master,
  initialMaterialId,
  initialOuterD,
  initialProductLength,
  initialProductWeight,
  initialRawPrice,
  initialStockLength,
  initialRemnantLength,
  onClose,
  onApply,
}: {
  master: CncMasterData;
  initialMaterialId: string;
  initialOuterD: string;
  initialProductLength?: string;
  initialProductWeight?: string;
  initialRawPrice?: string; // 메인 화면 원소재 단가 (₩/g) — 팝업은 원/kg 로 표시
  initialStockLength?: string; // 견적에 저장된 원자재 전장 (mm) — 없으면 2,500
  initialRemnantLength?: string; // 견적에 저장된 잔봉 길이 (mm) — 없으면 250
  onClose: () => void;
  onApply: (r: InputWeightApply) => void;
}) => {
  const [materialId, setMaterialId] = useState(initialMaterialId);
  const [outerD, setOuterD] = useState(initialOuterD);
  const [productLength, setProductLength] = useState(initialProductLength ?? "");
  // 절단 LOSS — 외경 입력 시 자동 조회, 단 사용자가 수기 입력하면 그 값을 우선한다(빈 값 = 자동).
  const [cutLossManual, setCutLossManual] = useState("");
  const [productWeight, setProductWeight] = useState(initialProductWeight ?? "");
  const [densityManual, setDensityManual] = useState("");
  // 원자재 전장/잔봉 — 기본값 2,500/250, 수정 가능, 견적별 저장(적용 시 반환).
  const [stockLength, setStockLength] = useState(
    initialStockLength?.trim() ? initialStockLength : String(DEFAULT_STOCK_LENGTH)
  );
  const [remnantLength, setRemnantLength] = useState(
    initialRemnantLength?.trim() ? initialRemnantLength : String(DEFAULT_REMNANT_LENGTH)
  );
  // 원자재 원가(원/kg) — 메인 원소재 단가(₩/g)×1000 으로 시작(수기 수정분 보존), 재질을 바꾸면
  // 마스터 단가로 자동 입력. 수정 또는 재질 변경 시에만 메인 원소재 단가를 덮어쓴다.
  const [rawPerKg, setRawPerKg] = useState(() => {
    const fromMain = perKgFromPerG(initialRawPrice ?? "");
    if (fromMain !== "" && num(fromMain) > 0) return fromMain;
    const m = master.raw.find((r) => r.id === initialMaterialId);
    return m ? clean(m.rawUnitPrice * 1000) : fromMain;
  });
  const [rawPriceDirty, setRawPriceDirty] = useState(false);
  const mobile = useIsMobile();

  const mat = master.raw.find((r) => r.id === materialId);
  const autoGPerMm3 = mat?.gPerMm3 ?? 0;
  const gPerMm3 = num(densityManual) > 0 ? num(densityManual) : autoGPerMm3;
  const autoCutLoss = outerD ? cutLossForOuter(num(outerD)) : 0;
  const cutLoss = cutLossManual !== "" ? num(cutLossManual) : autoCutLoss;

  const result = inputWeight({
    outerD: num(outerD),
    productLength: num(productLength),
    gPerMm3,
    cutLoss,
    stockLength: num(stockLength),
    remnantLength: num(remnantLength),
    rawPricePerKg: num(rawPerKg),
  });
  const cannotProduce = num(outerD) > 0 && num(productLength) > 0 && result.piecesPerBar === 0;

  const onMaterial = (id: string) => {
    setMaterialId(id);
    setDensityManual(""); // 재질 변경 시 자동 단중으로 복귀
    const m = master.raw.find((r) => r.id === id);
    if (m) {
      setRawPerKg(clean(m.rawUnitPrice * 1000)); // 재질 선택 → 마스터 단가 자동 입력
      setRawPriceDirty(true);
    }
  };

  const apply = () => {
    onApply({
      // 메인 화면에는 소수 둘째 자리 버림값(9.69) 저장 — 팝업 표시는 정밀값(9.6909).
      inputWeight: result.appliedWeightG ? String(result.appliedWeightG) : "",
      outerDiameter: outerD,
      materialId,
      productLength,
      stockLength,
      remnantLength,
      rawPrice: rawPriceDirty ? clean(num(rawPerKg) / 1000) : undefined,
    });
    onClose();
  };

  const ResultRow = ({
    label,
    formula,
    value,
  }: { label: string; formula: string; value: string }) => (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 10,
        padding: "10px 14px",
        borderRadius: 10,
        background: "var(--bg-surface-2)",
      }}
    >
      <span style={{ fontWeight: 700, fontSize: 13, minWidth: 90 }}>{label}</span>
      <span style={{ flex: 1, fontSize: 12, color: "var(--text-faint)" }}>{formula}</span>
      <span style={{ fontFamily: "monospace", fontWeight: 700, fontSize: 14 }}>{value}</span>
    </div>
  );

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.6)",
        zIndex: 60,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 20,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: 880,
          maxWidth: "100%",
          maxHeight: "90vh",
          overflowY: "auto",
          background: "var(--bg-surface)",
          borderRadius: 14,
          boxShadow: "rgba(0,0,0,0.5) 0px 8px 24px",
          padding: 24,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 18 }}>
          <Icon name="grid" size={18} color="var(--brand)" />
          <div style={{ fontFamily: "var(--font-title)", fontWeight: 800, fontSize: 18 }}>
            투입중량 계산
          </div>
          <div style={{ flex: 1 }} />
          <button
            type="button"
            onClick={onClose}
            style={{
              background: "transparent",
              border: 0,
              color: "var(--text-dim)",
              cursor: "pointer",
              display: "flex",
            }}
          >
            <Icon name="x" size={18} />
          </button>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: cols(mobile, "1fr 1fr"), gap: 24 }}>
          {/* 좌: 입력 정보 */}
          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            <div style={{ fontWeight: 800, fontSize: 14 }}>1. 입력 정보</div>
            <Field label="재질" en="Material">
              <Select
                value={materialId}
                onChange={onMaterial}
                options={master.raw.map((m) => ({ value: m.id, label: m.name }))}
                placeholder="선택"
              />
            </Field>
            <div style={{ display: "grid", gridTemplateColumns: cols(mobile, "1fr 1fr"), gap: 12 }}>
              <Field label="외경" en="mm" hint="외경 입력 시 절단 LOSS 자동 조회">
                <TextInput value={outerD} onChange={setOuterD} type="number" mono align="right" />
              </Field>
              <Field label="제품 길이" en="mm">
                <TextInput
                  value={productLength}
                  onChange={setProductLength}
                  type="number"
                  mono
                  align="right"
                />
              </Field>
              <Field label="절단 LOSS" en="mm" hint={`자동: ${fmtDec(autoCutLoss, 2)} (수정 가능)`}>
                <TextInput
                  value={cutLossManual}
                  onChange={setCutLossManual}
                  type="number"
                  mono
                  align="right"
                  placeholder={String(autoCutLoss)}
                />
              </Field>
              <Field
                label="단중"
                en="g/mm³"
                hint={mat ? "재질 자동 조회" : "재질 선택 또는 직접 입력"}
              >
                <TextInput
                  value={densityManual}
                  onChange={setDensityManual}
                  type="number"
                  mono
                  align="right"
                  placeholder={String(autoGPerMm3)}
                />
              </Field>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: cols(mobile, "1fr 1fr"), gap: 12 }}>
              <Field label="원자재 전장" en="mm" hint="기본값 · 수정 가능">
                <TextInput
                  value={stockLength}
                  onChange={setStockLength}
                  type="number"
                  mono
                  align="right"
                />
              </Field>
              <Field label="잔봉 길이" en="mm" hint="기본값 · 수정 가능">
                <TextInput
                  value={remnantLength}
                  onChange={setRemnantLength}
                  type="number"
                  mono
                  align="right"
                />
              </Field>
            </div>
            <Field
              label="원자재 원가"
              en="원/kg"
              hint="재질 선택 시 마스터 단가 자동 · 수정 시 메인 화면 원소재 단가도 변경"
            >
              <TextInput
                value={rawPerKg}
                onChange={(v) => {
                  setRawPerKg(v);
                  setRawPriceDirty(true);
                }}
                type="number"
                mono
                align="right"
              />
            </Field>
            <Field label="제품중량 (참고)" en="g">
              <TextInput
                value={productWeight}
                onChange={setProductWeight}
                type="number"
                mono
                align="right"
              />
            </Field>

            {/* 절단 LOSS 자동 적용 기준 */}
            <div style={{ marginTop: 4 }}>
              <div
                style={{
                  fontSize: 12,
                  fontWeight: 700,
                  color: "var(--text-muted)",
                  marginBottom: 6,
                }}
              >
                절단 LOSS 자동 적용 기준 (외경 기준)
              </div>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                <thead>
                  <tr style={{ color: "var(--text-dim)" }}>
                    <th style={cellL}>외경 범위 (mm)</th>
                    <th style={cellR}>절단 LOSS (mm)</th>
                  </tr>
                </thead>
                <tbody>
                  {CUT_WIDTH_STEPS.map(([upper, loss], i) => {
                    const lower = i === 0 ? 2 : CUT_WIDTH_STEPS[i - 1][0] + 1;
                    return (
                      <tr key={upper} style={{ borderTop: "1px solid var(--bg-surface-2)" }}>
                        <td style={cellL}>{`Ø${lower} ~ Ø${upper}`}</td>
                        <td style={cellR}>{loss.toFixed(1)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              <div style={{ fontSize: 11, color: "var(--text-faint)", marginTop: 4 }}>
                ※ 외경 입력 시 해당 범위의 절단 LOSS 가 자동 적용됩니다(범위 초과 시 마지막 값).
              </div>
            </div>
          </div>

          {/* 우: 계산 결과 + 단중표 */}
          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            <div style={{ fontWeight: 800, fontSize: 14 }}>2. 계산 결과 (1본 기준)</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              <ResultRow
                label="절단 길이"
                formula="제품 길이 + 절단 LOSS"
                value={`${fmtDec(result.cutLength, 2)} mm`}
              />
              <ResultRow
                label="유효 길이"
                formula="원자재 전장 − 잔봉 길이"
                value={`${fmtDec(result.effectiveLength, 2)} mm`}
              />
              <ResultRow
                label="1본 중량"
                formula={`(외경 ÷ 2)² × ${INPUT_PI} × 단중 × 원자재 전장`}
                value={`${fmtInt(result.barWeightG)} g`}
              />
              <ResultRow
                label="1본 생산량"
                formula="유효 길이 ÷ 절단 길이 → 소수점 절삭"
                value={`${result.piecesPerBar.toLocaleString("ko-KR")} 개/본`}
              />
              <ResultRow
                label="1본 자재원가"
                formula="원자재 원가 × 1본 중량(g) ÷ 1,000"
                value={`${fmtInt(result.barCost)} 원/본`}
              />
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  padding: "12px 14px",
                  borderRadius: 10,
                  background: "var(--bg-card-hover)",
                }}
              >
                <span style={{ fontWeight: 800, fontSize: 14, minWidth: 90 }}>
                  투입중량 (잔봉 반영)
                </span>
                <span style={{ flex: 1, fontSize: 12, color: "var(--text-faint)" }}>
                  1본 중량 ÷ 1본 생산량
                </span>
                <span
                  style={{
                    fontFamily: "monospace",
                    fontWeight: 800,
                    fontSize: 18,
                    color: "var(--brand)",
                  }}
                >
                  {fmtDec(result.weightG, 4)} g
                </span>
              </div>
            </div>
            {cannotProduce ? (
              <div style={{ fontSize: 11.5, color: "var(--text-negative)", fontWeight: 700 }}>
                ⚠ 1본 생산량이 0 입니다 — 제품 길이·절단 LOSS·원자재 전장·잔봉 길이를 확인하세요.
              </div>
            ) : (
              <div style={{ fontSize: 11.5, color: "var(--text-muted)" }}>
                ℹ 적용 시 메인 재료비 화면의 '투입중량 g'에{" "}
                <b>{result.appliedWeightG ? fmtDec(result.appliedWeightG, 2) : "—"}</b> 자동 반영
                (소수 둘째 자리 버림).
              </div>
            )}

            <div style={{ marginTop: 4 }}>
              <div
                style={{
                  fontSize: 12,
                  fontWeight: 700,
                  color: "var(--text-muted)",
                  marginBottom: 6,
                }}
              >
                원재료 단중표 (원가 마스터)
              </div>
              <div style={{ maxHeight: 180, overflowY: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                  <thead>
                    <tr style={{ color: "var(--text-dim)" }}>
                      <th style={cellL}>재질</th>
                      <th style={cellR}>비중 (g/cm³)</th>
                      <th style={cellR}>단중 (g/mm³)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {master.raw.map((m) => (
                      <tr
                        key={m.id}
                        style={{
                          borderTop: "1px solid var(--bg-surface-2)",
                          background: m.id === materialId ? "var(--bg-surface-2)" : "transparent",
                          color: m.id === materialId ? "var(--brand)" : "var(--text-near)",
                          fontWeight: m.id === materialId ? 700 : 400,
                        }}
                      >
                        <td style={cellL}>{m.name}</td>
                        <td style={cellR}>{fmtDec(m.density, 3)}</td>
                        <td style={cellR}>{fmtDec(m.gPerMm3, 5)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div style={{ fontSize: 11, color: "var(--text-faint)", marginTop: 4 }}>
                ※ 단중표에 없는 재질은 CNC 마스터에서 등록 후 사용하세요.
              </div>
            </div>
          </div>
        </div>

        <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 22 }}>
          <button
            type="button"
            onClick={onClose}
            style={{
              padding: mobile ? "12px 20px" : "10px 20px",
              borderRadius: 12,
              border: 0,
              background: "var(--bg-surface-2)",
              color: "var(--text-base)",
              fontWeight: 700,
              cursor: "pointer",
              fontFamily: "inherit",
              fontSize: 14,
            }}
          >
            취소
          </button>
          <button
            type="button"
            onClick={apply}
            style={{
              padding: mobile ? "12px 24px" : "10px 24px",
              borderRadius: 12,
              border: 0,
              background: "var(--brand)",
              color: "#fff",
              fontWeight: 700,
              cursor: "pointer",
              fontFamily: "inherit",
              fontSize: 14,
            }}
          >
            적용
          </button>
        </div>
      </div>
    </div>
  );
};
